import { useMemo } from 'react';
import { RequestStatus, SLA_WARN_HOURS, type RequestKind } from '@magimp/shared';
import { getDefaultRequestListStatuses, type StatusFilterRole } from '../filters.js';
import type { RequestView } from '../types.js';

type StatusFilterId = 'all' | RequestStatus;
type SlaFilterId = 'hot' | 'overdue';

export interface RequestChipCountsInput {
  requests: readonly RequestView[];
  selectedStatuses: readonly RequestStatus[];
  selectedSlaStates: readonly SlaFilterId[];
  selectedKind: RequestKind | null;
  unreadOnly: boolean;
  unreadById: ReadonlyMap<string, { unreadCount: number; isMarkedUnread: boolean }>;
  now: number;
  statusFilterIds: readonly StatusFilterId[];
  slaFilterIds: readonly SlaFilterId[];
  kindFilterIds: readonly RequestKind[];
  // WHY (#1159 PR5): the All chip count must reflect the role-aware
  //   default status set so a logist's All count includes only visible
  //   statuses. Caller resolves the role from `useAuthStore`; tests pass
  //   an explicit role.
  statusFilterRole: StatusFilterRole;
}

/**
 * @aiPurpose chip-badge count map for `RequestsListPage`. Computes
 *   counts off the LOADED page set so a chip for a status outside the
 *   current wire scope returns `undefined` (UI omits the badge) rather
 *   than showing a misleading `0`.
 * @aiInvariant the All chip mirrors the visible-row predicate; SLA
 *   counts mirror backend active-SLA semantics (#962) — only live
 *   countdown statuses (READY_FOR_WORK / IN_WORK) are countable; the
 *   Unread chip count hides when active OR zero, matching the
 *   `UnreadBadge` render rule (#583); kind chip counts follow the
 *   same active-or-zero suppression (#1054).
 */
export function useRequestChipCounts(
  input: RequestChipCountsInput,
): Record<string, number | undefined> {
  const {
    requests,
    selectedStatuses,
    selectedSlaStates,
    selectedKind,
    unreadOnly,
    unreadById,
    now,
    statusFilterIds,
    slaFilterIds,
    kindFilterIds,
    statusFilterRole,
  } = input;
  return useMemo(() => {
    const allowed: readonly RequestStatus[] =
      selectedStatuses.length === 0
        ? getDefaultRequestListStatuses(statusFilterRole)
        : selectedStatuses;
    const result: Record<string, number | undefined> = {
      all: requests.filter((r) => allowed.includes(r.status)).length,
    };
    for (const id of statusFilterIds) {
      if (id === 'all') continue;
      result[id] = allowed.includes(id)
        ? requests.filter((r) => r.status === id).length
        : undefined;
    }
    const allowedSla = selectedSlaStates.length === 0 ? ['hot', 'overdue'] : selectedSlaStates;
    for (const id of slaFilterIds) {
      result[id] = allowedSla.includes(id)
        ? requests.filter((r) => {
            if (r.status !== RequestStatus.READY_FOR_WORK && r.status !== RequestStatus.IN_WORK)
              return false;
            if (!r.slaDeadline) return false;
            return id === 'hot'
              ? r.slaDeadline > now && r.slaDeadline < now + SLA_WARN_HOURS * 60 * 60 * 1000
              : r.slaDeadline <= now;
          }).length
        : undefined;
    }
    const unreadLoadedCount = requests.filter((r) => {
      const u = unreadById.get(r.id);
      if (!u) return false;
      return u.unreadCount > 0 || u.isMarkedUnread;
    }).length;
    result['unread'] = unreadOnly || unreadLoadedCount === 0 ? undefined : unreadLoadedCount;
    for (const id of kindFilterIds) {
      if (selectedKind === id) {
        result[`kind:${id}`] = undefined;
        continue;
      }
      const c = requests.filter((r) => r.kind === id).length;
      result[`kind:${id}`] = c === 0 ? undefined : c;
    }
    return result;
  }, [
    requests,
    selectedStatuses,
    selectedSlaStates,
    selectedKind,
    unreadOnly,
    unreadById,
    now,
    statusFilterIds,
    slaFilterIds,
    kindFilterIds,
    statusFilterRole,
  ]);
}
